import React from 'react';

const AuditLogStats = ({ data, loading }) => {
    
    // Formatting helpers
    const formatNumber = (num) => {
        return num !== undefined && num !== null ? num.toLocaleString() : '0';
    };

    const formatRam = (ramObj) => {
        if (!ramObj) return 'N/A';
        const rss = ramObj.memory?.delta?.rss ? ramObj.memory.delta.rss.toFixed(2) : '0.00';
        const heap = ramObj.memory?.delta?.heapUsed ? ramObj.memory.delta.heapUsed.toFixed(2) : '0.00';
        return (
            <div className="stats-ram-display">
                <div className="stats-ram-rss" style={{ color: '#0ea5e9', fontWeight: 600 }}>{rss} MB RSS</div>
                <div className="stats-ram-heap" style={{ color: '#8b5cf6', fontSize: '12px' }}>{heap} MB Heap</div>
            </div>
        );
    };

    const formatTime = (timeObj) => {
        if (!timeObj) return 'N/A';
        return (
            <span style={{ fontWeight: 600, color: '#334155' }}>
                {Number(timeObj.responseTime).toFixed(2)} ms
            </span>
        );
    };

    const renderDetails = (obj) => {
        if (!obj) return null;
        return (
            <div style={{ marginTop: '10px', fontSize: '11px', color: '#64748b', borderTop: '1px solid #f1f5f9', paddingTop: '8px' }}>
                <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: '4px' }} title={obj.path}>
                    <i className="fa fa-link" style={{ marginRight: '6px' }}></i>
                    {obj.path || 'N/A'}
                </div>
                <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: '4px' }} title={obj.feature}>
                    <i className="fa fa-tag" style={{ marginRight: '6px' }}></i>
                    {obj.feature || 'N/A'}
                </div>
                <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={obj.userId}>
                    <i className="fa fa-user" style={{ marginRight: '6px' }}></i>
                    {obj.userId || 'N/A'}
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="audit-stats-container skeleton-loading">
                <div className="d-flex justify-content-center my-4">
                    <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">Loading Stats...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (!data) return null;

    return (
        <div className="audit-stats-container">
            {/* ROW 1: Key Metrics */}
            <div className="audit-stats-row1">
                <div className="audit-stats-card">
                    <div className="stats-icon primary-bg">
                        <i className="fa fa-chart-line"></i>
                    </div>
                    <div className="stats-content">
                        <div className="stats-label">Total API Calls</div>
                        <div className="stats-value" style={{ fontSize: '24px', fontWeight: 700 }}>{formatNumber(data.totalCalls)}</div>
                    </div>
                </div>

                <div className="audit-stats-card">
                    <div className="stats-icon danger-bg">
                        <i className="fa fa-microchip"></i>
                    </div>
                    <div className="stats-content">
                        <div className="stats-label">Highest RAM Usage</div>
                        <div className="stats-value">{formatRam(data.highestRam)}</div>
                        {renderDetails(data.highestRam)}
                    </div>
                </div>

                <div className="audit-stats-card">
                    <div className="stats-icon warning-bg">
                        <i className="fa fa-memory"></i>
                    </div>
                    <div className="stats-content">
                        <div className="stats-label">Highest Heap Usage</div>
                        <div className="stats-value">{formatRam(data.highestHeap)}</div>
                        {renderDetails(data.highestHeap)}
                    </div>
                </div>

                <div className="audit-stats-card">
                    <div className="stats-icon info-bg">
                        <i className="fa fa-stopwatch"></i>
                    </div>
                    <div className="stats-content">
                        <div className="stats-label">Highest Response Time</div>
                        <div className="stats-value">{formatTime(data.highestResponseTime)}</div>
                        {renderDetails(data.highestResponseTime)}
                    </div>
                </div>
            </div>

            {/* ROW 2: Distributions */}
            <div className="audit-stats-row2">
                <div className="audit-stats-section">
                    <h4 className="stats-section-title">Methods</h4>
                    <div className="stats-distribution-grid">
                        <div className="stats-dist-item">
                            <span className="audit-badge badge-get">GET</span>
                            <span className="stats-dist-count">{formatNumber(data.methodCounts?.GET)}</span>
                        </div>
                        <div className="stats-dist-item">
                            <span className="audit-badge badge-post">POST</span>
                            <span className="stats-dist-count">{formatNumber(data.methodCounts?.POST)}</span>
                        </div>
                        <div className="stats-dist-item">
                            <span className="audit-badge badge-put">PUT</span>
                            <span className="stats-dist-count">{formatNumber(data.methodCounts?.PUT)}</span>
                        </div>
                        <div className="stats-dist-item">
                            <span className="audit-badge badge-delete">DELETE</span>
                            <span className="stats-dist-count">{formatNumber(data.methodCounts?.DELETE)}</span>
                        </div>
                    </div>
                </div>

                <div className="audit-stats-section">
                    <h4 className="stats-section-title">Status Codes</h4>
                    <div className="stats-distribution-grid">
                        <div className="stats-dist-item">
                            <span className="audit-badge status-success">2xx</span>
                            <span className="stats-dist-count">{formatNumber(data.statusCounts?.['2xx'])}</span>
                        </div>
                        <div className="stats-dist-item">
                            <span className="audit-badge status-redirect">3xx</span>
                            <span className="stats-dist-count">{formatNumber(data.statusCounts?.['3xx'])}</span>
                        </div>
                        <div className="stats-dist-item">
                            <span className="audit-badge status-client-err">4xx</span>
                            <span className="stats-dist-count">{formatNumber(data.statusCounts?.['4xx'])}</span>
                        </div>
                        <div className="stats-dist-item">
                            <span className="audit-badge status-server-err">5xx</span>
                            <span className="stats-dist-count">{formatNumber(data.statusCounts?.['5xx'])}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AuditLogStats;
